import { ReactNode, useState } from 'react';
import { SerializedStyles } from '@emotion/react';
import {
  Blip,
  SubmitFeedbackRequest_Rating,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import { NetworkPerformance } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import dayjs from 'dayjs';
import { CallQualityV1 } from '@frontend/api-call-quality';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Accordion,
  Button,
  Chip,
  Heading,
  Modal,
  Text,
  TextareaField,
  useAlert,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { HydratedCallRecord } from '../types';
import { barTypes } from './bar-types';
import { formatDuration } from './call-quality-utils';
import { metricInfo } from './const';
import { METRIC_VALUE_ICONS } from './icon-const';
import { SimpleTimelineChart, DotWithLabel } from './timeline-chart';
import { MetricData, MetricValue, TimelineBarDetail, TimelineDataItem } from './types';

export const StablePerformanceSection = ({ item }: { item: HydratedCallRecord }) => {
  const { t } = useTranslation('calls');
  const heading = t('Stable Performance');

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          gridTemplateColumns: 'max-content max-content',
          alignItems: 'center',
        }}
      >
        <Icon name='speed' color='success' />
        <Heading level={3}>{heading}</Heading>
      </div>
      <Text size='medium'>{t('Loud and clear – your connection was top-notch!')}</Text>
      <CallQualityFeedback
        detectedQuality={NetworkPerformance.NETWORK_PERFORMANCE_GOOD}
        channelId={item.channelId ?? ''}
        locationId={item.locationId ?? ''}
        startTime={item.startedAt ?? ''}
      />
    </div>
  );
};

export const IssuesDetectedSection = ({ variant }: { variant: 'preview' | 'screen' }) => {
  const { t } = useTranslation('calls');
  const heading = t('Issues Detected');

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          gridTemplateColumns: 'max-content max-content',
          alignItems: 'center',
        }}
      >
        <Icon name='speed' color='warn' />
        {variant === 'preview' ? (
          <Text size='large' weight='bold'>
            {heading}
          </Text>
        ) : (
          <Heading level={3}>{heading}</Heading>
        )}
      </div>
      <Text size={variant === 'preview' ? 'medium' : 'large'}>
        {t(
          `We've detected issues with your network that may have affected the audio quality of this call. You and your caller may have experienced choppy, distorted, or delayed audio. `
        )}
      </Text>
    </div>
  );
};

type CallQualityAccordionItemOldProps = {
  itemClassname?: SerializedStyles;
  headerClassname?: SerializedStyles;
  onClick?: () => void;
  hasIssues?: boolean;
  item: HydratedCallRecord;
};

/**
 * @deprecated This old accordion item is deprecated and will be removed in a future release. Please use `CallQualityAccordionItem`
 */
export const CallQualityAccordionItemOld = ({
  itemClassname,
  headerClassname,
  onClick,
  hasIssues = false,
  item,
}: CallQualityAccordionItemOldProps) => {
  const { t } = useTranslation('calls');
  return (
    <Accordion.Item title={t('Network Performance')} value='performance' css={itemClassname}>
      <Accordion.Header css={headerClassname}>
        <div css={{ display: 'grid' }}>
          <Text weight='bold' css={{ fontSize: theme.fontSize(18) }}>
            {t('Network Performance')}
          </Text>
          {hasIssues ? (
            <Chip variant='warn' css={{ width: 'max-content', maxWidth: 'max-content' }}>
              {t('Issues Detected')}
            </Chip>
          ) : (
            <Chip variant='success' css={{ width: 'max-content' }}>
              {t('Stable')}
            </Chip>
          )}
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <div css={{ display: 'grid', gap: theme.spacing(2) }}>
          {hasIssues ? (
            <div css={{ display: 'grid', gap: theme.spacing(2) }}>
              <IssuesDetectedSection variant='preview' />
              <Button
                variant='tertiary'
                onClick={onClick}
                css={{ justifySelf: 'start' }}
                trackingId='callinsights-networkperformance-issuesdetected'
              >
                {t('View Details')}
              </Button>
              <CallQualityFeedback
                channelId={item.channelId ?? ''}
                locationId={item.locationId ?? ''}
                startTime={item.startedAt ?? ''}
              />
            </div>
          ) : (
            <StablePerformanceSection item={item} />
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const deskphoneRecommendations = [
  i18next.t('Restart your phones periodically', { ns: 'calls' }),
  i18next.t('Limit heavy internet use during busy hours', { ns: 'calls' }),
  i18next.t('Ensure that your network equipment (router and/or switch) is set up properly', { ns: 'calls' }),
  i18next.t('Ensure that your network equipment is not overloaded with plugged in devices', { ns: 'calls' }),
];

const softphoneRecommendations = [
  i18next.t('Close unused browser tabs to reduce internet traffic', { ns: 'calls' }),
  i18next.t('Close unused running apps on your computer', { ns: 'calls' }),
  i18next.t('Limit the number of devices using your internet', { ns: 'calls' }),
  i18next.t('Conduct an internet speed test', { ns: 'calls' }),
];

export const RecommendationsSection = () => {
  const { t } = useTranslation('calls');
  const heading = t('Recommendations');

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <Heading level={3}>{heading}</Heading>
      <Text size='large' weight='bold'>
        {t('For Desk Phone')}
      </Text>
      <ul
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          paddingInlineStart: theme.spacing(2),
        }}
      >
        {deskphoneRecommendations.map((recommendation) => (
          <li key={recommendation}>
            <Text size='large' key={recommendation}>
              {recommendation}
            </Text>
          </li>
        ))}
      </ul>
      <Text size='large' weight='bold'>
        {t('For Softphone')}
      </Text>
      <ul
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          paddingInlineStart: theme.spacing(2),
        }}
      >
        {softphoneRecommendations.map((recommendation) => (
          <li key={recommendation}>
            <Text size='large' key={recommendation}>
              {recommendation}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MetricItem = ({
  title,
  values,
  onInfoClick,
}: {
  title: string;
  values: MetricValue[];
  onInfoClick: () => void;
}) => {
  return (
    <div css={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: 'span 2' }}>
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridTemplateColumns: 'max-content max-content',
          gap: theme.spacing(1),
        }}
      >
        <Text>{title}</Text>
        <Button
          onClick={onInfoClick}
          css={{ width: 16, height: 16, padding: 0 }}
          size='small'
          iconName='info-small'
          variant='secondary'
        />
      </div>
      <Text
        weight='bold'
        as='div'
        css={{
          display: 'grid',
          gap: theme.spacing(2),
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        {values.map(({ sentiment, label, direction }, index) => (
          <MetricValueBadge key={`title-${index}`} direction={direction} sentiment={sentiment} label={label} />
        ))}
      </Text>
    </div>
  );
};

const MetricValueBadge = ({ sentiment, label, direction }: MetricValue) => {
  const icon = METRIC_VALUE_ICONS[sentiment];
  return (
    <div
      css={{
        display: 'grid',
        alignItems: 'center',
        gridTemplateColumns: 'max-content max-content',
        gap: theme.spacing(1),
      }}
    >
      {icon}
      <Text
        size='large'
        weight='bold'
        css={{
          display: 'grid',
          gap: theme.spacing(0.5),
          gridTemplateColumns: 'max-content max-content',
          alignItems: 'center',
        }}
      >
        {direction && <Icon name={direction === 'inbound' ? 'trend-down-small' : 'trend-up-small'} />}
        {label}
      </Text>
    </div>
  );
};

export const MetricSection = ({ data, subtitle }: { data: MetricData[]; subtitle: ReactNode }) => {
  const { t } = useTranslation('calls');
  const [modalView, setModalView] = useState<keyof typeof metricInfo>('mosMin');
  const heading = t('Call Metrics');
  const { modalProps, closeModal, openModal } = useModalControl();

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div>
        <Heading level={3}>{heading}</Heading>
        {typeof subtitle === 'string' ? (
          <Text size='medium' color='light'>
            {subtitle}
          </Text>
        ) : (
          subtitle
        )}
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'max-content max-content',
          columnGap: theme.spacing(3),
          rowGap: theme.spacing(1),
          paddingInline: theme.spacing(2),
        }}
      >
        {data.map(({ type, values }) => (
          <MetricItem
            key={type}
            title={metricInfo[type].title}
            values={values}
            onInfoClick={() => {
              setModalView(type);
              openModal();
            }}
          />
        ))}
        <Modal {...modalProps}>
          <Modal.Header onClose={closeModal}>{metricInfo[modalView].title}</Modal.Header>
          <Modal.Body>
            {metricInfo[modalView].description.map((text, index) => (
              <Text key={index} size='medium'>
                {text}
              </Text>
            ))}
          </Modal.Body>
        </Modal>
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'max-content max-content',
          gap: theme.spacing(2),
          paddingInline: theme.spacing(2),
        }}
      >
        <div css={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gap: theme.spacing(1) }}>
          <Icon name='trend-down-small' />
          <Text weight='bold' size='small' color='subdued'>
            {t('Inbound audio stream')}
          </Text>
        </div>
        <div css={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gap: theme.spacing(1) }}>
          <Icon name='trend-up-small' />
          <Text weight='bold' size='small' color='subdued'>
            {t('Outbound audio stream')}
          </Text>
        </div>
      </div>
    </div>
  );
};

export const TimelineSection = ({
  startTime,
  endTime,
  blips,
  onClick,
}: {
  startTime: string;
  endTime: string;
  blips: Blip[];
  onClick: (data: { label: string; details?: TimelineBarDetail[]; blip: Blip }) => void;
}) => {
  const { t } = useTranslation('calls');
  const heading = t('Call Quality');

  const segments: TimelineDataItem<typeof barTypes>[] = generateCallQualitySegments({
    endTime,
    startTime,
    blips,
    onClick,
  });

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <Heading level={3}>{heading}</Heading>
      {segments.length > 0 ? (
        <SimpleTimelineChart
          barTypes={barTypes}
          data={segments}
          axis={{
            startLabel: dayjs(startTime).format('hh:mm A'),
            endLabel: dayjs(endTime).format('hh:mm A'),
          }}
          legends={[
            { label: t('Stable Quality'), color: theme.colors.success20 },
            { label: t('Low Quality'), color: theme.colors.critical30 },
          ]}
        />
      ) : (
        <div
          css={{
            display: 'grid',
            gap: theme.spacing(2),
            alignItems: 'center',
          }}
        >
          <Text size='large'>{t('Not enough call quality data to display a timeline.')}</Text>
        </div>
      )}
    </div>
  );
};

function createPositiveSegment(startTime: string, endTime: string, totalDuration: number) {
  return createSegment({
    startTime,
    endTime,
    totalDuration,
    type: 'positive',
    details: [
      {
        id: 'callQuality',
        title: i18next.t('Call Quality', { ns: 'calls' }),
        value: 'high',
        label: <DotWithLabel label={i18next.t('High', { ns: 'calls' })} color={theme.colors.success20} />,
      },
    ],
  });
}

function createSegment({
  startTime,
  endTime,
  totalDuration,
  type,
  details,
  onClick,
}: {
  startTime: string;
  endTime: string;
  totalDuration: number;
  type: keyof typeof barTypes;
  details: TimelineBarDetail[];
  onClick?: (data: { label: string; details?: TimelineBarDetail[] }) => void;
}): TimelineDataItem<typeof barTypes> {
  const segmentDuration = new Date(endTime).getTime() - new Date(startTime).getTime();
  return {
    label: dayjs(startTime).format('hh:mm:ss A') + ' - ' + dayjs(endTime).format('hh:mm:ss A'),
    // We default to flooring the width as a good enough approximation, then we add the remaining width to the last segment
    width: `${Math.floor((segmentDuration / totalDuration) * 100)}%`,
    type,
    details,
    onClick,
  };
}

type GenerateCallQualitySegmentsProps = {
  endTime: string;
  startTime: string;
  blips: Blip[];
  onClick: (data: { label: string; details?: TimelineBarDetail[]; blip: Blip }) => void;
};

export function generateCallQualitySegments({ endTime, startTime, blips, onClick }: GenerateCallQualitySegmentsProps) {
  const shouldPadStart = blips[0]?.startTime !== startTime;
  // Adjust endTime if last blip extends beyond it
  // We will use the endTime of the last blip as the new endTime instead of the provided endTime
  let _endTime = endTime;
  const lastBlip = blips[blips.length - 1];
  if (lastBlip?.endTime && new Date(lastBlip.endTime).getTime() > new Date(endTime).getTime()) {
    _endTime = lastBlip.endTime;
  }
  const shouldPadEnd = lastBlip?.endTime !== _endTime;

  const totalDuration = new Date(_endTime).getTime() - new Date(startTime).getTime();

  // Initialize array to store segments with their percentages
  const segments = [];

  // Add padding at start if needed
  if (shouldPadStart && blips[0]?.startTime) {
    segments.push(createPositiveSegment(startTime, blips[0].startTime, totalDuration));
  }

  // Process each blip and add padding between blips
  blips.forEach((blip, index) => {
    // Add the blip segment
    const blipDuration = new Date(blip.endTime).getTime() - new Date(blip.startTime).getTime();
    segments.push(
      createSegment({
        startTime: blip.startTime,
        endTime: blip.endTime,
        totalDuration,
        type: 'negative',
        details: [
          {
            id: 'callQuality',
            title: i18next.t('Call Quality', { ns: 'calls' }),
            value: 'low',
            label: <DotWithLabel label='Low' color={theme.colors.critical30} />,
          },
          {
            id: 'mosAvg',
            title: i18next.t('MOS (avg.)', { ns: 'calls' }),
            value: blip.avgMosScore,
            label: (
              <span>
                <Text as='span' size='medium' weight='bold' color='error'>
                  {blip.avgMosScore.toFixed(2)}{' '}
                </Text>
                <Text as='span' size='medium' weight='bold'>
                  / 5
                </Text>
              </span>
            ),
          },
          {
            id: 'duration',
            title: i18next.t('Duration', { ns: 'calls' }),
            value: blipDuration,
            label: formatDuration(blipDuration),
          },
        ],
        onClick: ({ label, details }) => {
          onClick({ label, details, blip });
        },
      })
    );

    // Add padding between blips if not the last blip
    if (index < blips.length - 1 && blips[index + 1]?.startTime) {
      segments.push(createPositiveSegment(blip.endTime, blips[index + 1].startTime, totalDuration));
    }
  });

  // Add padding at end if needed
  if (shouldPadEnd && lastBlip?.endTime) {
    segments.push(createPositiveSegment(lastBlip.endTime, _endTime, totalDuration));
  }

  // Adjust the last segment to fill any remaining gap
  const totalWidth = segments.reduce((acc, segment) => acc + parseFloat(segment.width.replace('%', '')), 0);
  if (totalWidth < 100 && segments.length > 0) {
    const lastSegment = segments[segments.length - 1];
    lastSegment.width = `${parseFloat(lastSegment.width.replace('%', '')) + (100 - totalWidth)}%`;
  }

  return segments;
}

export const CallQualityFeedback = ({
  detectedQuality = NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED,
  startTime,
  channelId,
  locationId,
}: {
  detectedQuality?: NetworkPerformance;
  startTime: string;
  channelId: string;
  locationId: string;
}) => {
  const { t } = useTranslation('calls');
  const { triggerProps, modalProps } = useModalControl({
    disableReturnFocus: true,
  });
  const textareaField = useFormField({ type: 'text' });
  const [feedback, setFeedback] = useState<SubmitFeedbackRequest_Rating>(SubmitFeedbackRequest_Rating.RATING_UNKNOWN);
  const alerts = useAlert();

  const { mutate } = CallQualityV1.Mutations.useSubmitFeedbackMutation({
    options: {
      onSuccess: () => {
        alerts.success({
          message: t('Feedback submitted successfully. Thank you!'),
        });
      },
      onError: () => {
        alerts.error({
          message: t('Failed to submit feedback. Please try again.'),
        });
      },
      onSettled: () => {
        textareaField.onChange({ value: '', name: 'feedback' });
        setFeedback(SubmitFeedbackRequest_Rating.RATING_UNKNOWN);
        modalProps.onClose();
      },
    },
  });

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, max-content)',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        border: `1px solid ${theme.colors.neutral20}`,
        width: 'max-content',
        borderRadius: theme.borderRadius.full,
      }}
    >
      {detectedQuality !== NetworkPerformance.NETWORK_PERFORMANCE_GOOD && (
        <Text size='medium'>{t('Is this helpful?')}</Text>
      )}
      <Button
        css={{ padding: 0, width: 24, height: 24 }}
        iconName='feedback-small'
        variant='secondary'
        {...triggerProps}
        onClick={() => {
          setFeedback(SubmitFeedbackRequest_Rating.RATING_POSITIVE);
          triggerProps.onClick();
        }}
        trackingId='call insights-networkperformance-feedback'
      />
      <Button
        css={{ padding: 0, width: 24, height: 24 }}
        iconName='feedback-bad-small'
        variant='secondary'
        {...triggerProps}
        onClick={() => {
          setFeedback(SubmitFeedbackRequest_Rating.RATING_NEGATIVE);
          triggerProps.onClick();
        }}
        trackingId='call insights-networkperformance-feedback'
      />
      <Modal {...modalProps} maxWidth={800}>
        <Modal.Header onClose={modalProps.onClose}>{t('Provide Additional Feedback')}</Modal.Header>
        <Modal.Body>
          <Text size='large'>
            {feedback === SubmitFeedbackRequest_Rating.RATING_POSITIVE
              ? t('Please share details about what you found valuable in the Network Performance information.')
              : t('Please share details about the issues in the Network Performance information.')}
          </Text>
          <TextareaField {...textareaField} label={t('Feedback (Optional)')} name='feedback' />
        </Modal.Body>
        <Modal.Footer
          primary={{
            label: t('Submit'),
            onClick: () => {
              mutate({
                callStartedAt: startTime,
                channelId,
                locationId,
                rating: feedback,
                feedbackText: textareaField.value,
                networkPerformance: detectedQuality,
              });
            },
          }}
        />
      </Modal>
    </div>
  );
};
